import {
  EntityType,
  Subscription,
} from "../../api/grpc/subscription/subscription";
import { Currencies } from "../../lib/helpers/getCurrencySymbol";
import { PRODUCT_STORE_SECTIONS } from "./ProductStore.catalog";
import {
  ProductStoreAddOnQuantity,
  ProductStoreArtwork,
  ProductStorePriceItem,
  ProductStorePricing,
  ProductStoreProduct,
  ProductStorePurchaseRule,
} from "./ProductStore.types";
import {
  formatPrice,
  resolveProductPrice,
  resolveShipping,
} from "./productStorePrices";
import { isOnPlan } from "./productStoreSubscription";

/** A product priced for this customer. */
export interface PurchaseProduct {
  id: string;
  title: string;
  description: string;
  artwork: ProductStoreArtwork;
  artworkAlt: string;
  /** e.g. "per device", or "per year" for subscription add-ons. */
  unitLabel: string;
  highlights: string[];
  priceId: string;
  unitCents: number;
  /** One unit in the customer's currency, e.g. "$695". */
  unitPrice: string;
  /** Subscription items renew with the subscription; the rest are paid once. */
  kind: "one-time" | "subscription";
  entityType?: EntityType;
}

export interface PurchaseAddOn extends PurchaseProduct {
  quantity: ProductStoreAddOnQuantity;
  quantityLabel?: string;
  recommended: boolean;
  /** Selecting this add-on also selects, and locks, that one. */
  requires?: string;
}

/** What the order dialog offers for a product. */
export interface PurchaseOffer {
  product: PurchaseProduct;
  quantity: ProductStorePurchaseRule["quantity"];
  addOns: PurchaseAddOn[];
  /** One per order. */
  shipping?: ProductStorePriceItem;
  currency: Currencies;
  /** Why the order can't be finished, e.g. "Requires BusinessPLUS plan". */
  blockedReason?: string;
}

const PRODUCTS = PRODUCT_STORE_SECTIONS.flatMap((section) => section.products);

/**
 * A catalog product with the customer's price, or undefined when it can't be
 * sold to them: no price, or a subscription add-on they already have.
 */
const getPurchaseProduct = (
  product: ProductStoreProduct,
  { prices, term, subscriptionAddOns = [] }: ProductStorePricing
): PurchaseProduct | undefined => {
  const kind =
    product.action.type === "subscription" ? "subscription" : "one-time";
  const owned =
    kind === "subscription" &&
    product.entityType !== undefined &&
    subscriptionAddOns.includes(product.entityType);
  const priceId = owned
    ? undefined
    : resolveProductPrice(product.pricing, prices, term)?.priceId;
  const item = prices.items.find((price) => price.priceId === priceId);

  if (!priceId || !item || !prices.currency) {
    return undefined;
  }

  return {
    id: product.id,
    title: product.title,
    description: product.description,
    artwork: product.artwork,
    artworkAlt: product.artworkAlt,
    unitLabel:
      kind === "subscription"
        ? term === "3-year"
          ? "per 3 years"
          : "per year"
        : product.unitLabel || "each",
    highlights: product.highlights || [],
    priceId,
    unitCents: item.priceCents,
    unitPrice: formatPrice(item.priceCents, prices.currency),
    kind,
    entityType: product.entityType,
  };
};

/**
 * What the order dialog offers for a product, following its purchase rule:
 * its quantity, the add-ons the customer can buy, and shipping. Undefined
 * when the product can't be ordered online by this customer, including when
 * its mandatory shipping has no price.
 */
export const getPurchaseOffer = (
  productId: string,
  pricing: ProductStorePricing,
  subscription?: Subscription
): PurchaseOffer | undefined => {
  const { mode, prices } = pricing;
  const catalogProduct = PRODUCTS.find((product) => product.id === productId);
  const rule = catalogProduct?.purchase;

  if (
    mode !== "direct" ||
    prices.loading ||
    prices.error ||
    !prices.currency ||
    !catalogProduct ||
    !rule
  ) {
    return undefined;
  }

  const product = getPurchaseProduct(catalogProduct, pricing);
  const shipping = rule.shipping && resolveShipping(rule.shipping, prices);

  if (!product || (rule.shipping && !shipping)) {
    return undefined;
  }

  const addOns = rule.addOns.flatMap((addOnRule): PurchaseAddOn[] => {
    const addOnProduct = PRODUCTS.find(({ id }) => id === addOnRule.productId);
    const priced = addOnProduct && getPurchaseProduct(addOnProduct, pricing);

    return priced
      ? [
          {
            ...priced,
            quantity: addOnRule.quantity,
            quantityLabel: addOnRule.quantityLabel,
            recommended: !!addOnRule.recommended,
            requires: addOnRule.requires,
          },
        ]
      : [];
  });
  const plan = rule.requiresPlan;

  return {
    product,
    quantity: rule.quantity,
    // An add-on that needs another one is only offered with it.
    addOns: addOns.filter(
      ({ requires }) => !requires || addOns.some(({ id }) => id === requires)
    ),
    shipping: shipping || undefined,
    currency: prices.currency,
    blockedReason:
      plan && !(subscription && isOnPlan(subscription, plan))
        ? `Requires ${plan} plan`
        : undefined,
  };
};

/** The offer for the product whose "Order now" dispatched this price ID. */
export const findPurchaseOffer = (
  priceId: string,
  pricing: ProductStorePricing,
  subscription?: Subscription
): PurchaseOffer | undefined => {
  // Match on the main price first, so only that product's offer is built.
  const product = PRODUCTS.find(
    ({ purchase, pricing: descriptor }) =>
      purchase &&
      resolveProductPrice(descriptor, pricing.prices, pricing.term)?.priceId ===
        priceId
  );

  return product && getPurchaseOffer(product.id, pricing, subscription);
};
